import React, { useState, useMemo } from 'react';
import {
  X,
  Bell,
  Send,
  Search,
  SlidersHorizontal,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const AccountManagerModal = ({ 
  onClose, 
  recentCandidates, 
  selectedCandidatesForEmail, 
  setSelectedCandidatesForEmail,
  sendingAccountManagerEmail,
  sendAccountManagerNotification 
}) => {

  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    name: '',
    email: '',
    client: '',
    bu: '',
    addedDateFrom: '',
    addedDateTo: '',
  });

  const updateFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

  const clearFilters = () => setFilters({
    name: '',
    email: '',
    client: '',
    bu: '',
    addedDateFrom: '',
    addedDateTo: '',
  });

  // Unique options for dropdowns
  const clientOptions = useMemo(() => {
    const vals = recentCandidates.map(c => c.client).filter(Boolean);
    return [...new Set(vals)].sort();
  }, [recentCandidates]);

  const buOptions = useMemo(() => {
    const vals = recentCandidates.map(c => c.bu || c.business_unit).filter(Boolean);
    return [...new Set(vals)].sort();
  }, [recentCandidates]);

  // Filtered candidates
  const filteredCandidates = useMemo(() => {
    return recentCandidates.filter(c => {
      const name = (c.candidate_name || c.candidateName || '').toLowerCase();
      const email = (c.candidate_email || c.candidateEmail || '').toLowerCase();
      const client = (c.client || '').toLowerCase();
      const bu = (c.bu || c.business_unit || '').toLowerCase();
      const startDate = c.start_date || c.startDate || '';

      if (filters.name && !name.includes(filters.name.toLowerCase())) return false;
      if (filters.email && !email.includes(filters.email.toLowerCase())) return false;
      if (filters.client && client !== filters.client.toLowerCase()) return false;
      if (filters.bu && bu !== filters.bu.toLowerCase()) return false;
      const addedDate = (c.created_at || '').substring(0, 10);
      if (filters.addedDateFrom && addedDate < filters.addedDateFrom) return false;
      if (filters.addedDateTo && addedDate > filters.addedDateTo) return false;

      return true;
    });
  }, [recentCandidates, filters]);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4 text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Bell className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Account Manager Notifications</h2>
                <p className="text-green-100 text-sm">Recent candidates added in the last 30 days</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 flex flex-col gap-4 overflow-y-auto">

          {/* Summary */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-green-900">Recent Candidates Summary</h3>
                <p className="text-green-700 text-sm">
                  Showing <span className="font-bold">{filteredCandidates.length}</span> of {recentCandidates.length} candidates added in the last 30 days
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-green-600">{selectedCandidatesForEmail.length}</div>
                <div className="text-sm text-green-700">Selected for notification</div>
              </div>
            </div>
          </div>

          {/* Filter Toggle */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-xl border text-sm font-medium transition-all ${
                showFilters || activeFilterCount > 0
                  ? 'bg-blue-50 border-blue-300 text-blue-700'
                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
              }`}
            >
              <SlidersHorizontal className="w-4 h-4" />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center w-5 h-5 bg-blue-600 text-white text-xs font-bold rounded-full">
                  {activeFilterCount}
                </span>
              )}
              {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="text-sm text-red-500 hover:text-red-700 font-medium transition-colors"
              >
                Clear all filters
              </button>
            )}
          </div>

          {/* Collapsible Filter Panel */}
          {showFilters && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              
              {/* Name */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Name</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={filters.name}
                    onChange={e => updateFilter('name', e.target.value)}
                    placeholder="Search by name..."
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Email</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={filters.email}
                    onChange={e => updateFilter('email', e.target.value)}
                    placeholder="Search by email..."
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                </div>
              </div>

              {/* Client */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Client</label>
                <select
                  value={filters.client}
                  onChange={e => updateFilter('client', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">All Clients</option>
                  {clientOptions.map(c => (
                    <option key={c} value={c.toLowerCase()}>{c}</option>
                  ))}
                </select>
              </div>

              {/* BU */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Business Unit (BU)</label>
                <select
                  value={filters.bu}
                  onChange={e => updateFilter('bu', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">All BUs</option>
                  {buOptions.map(b => (
                    <option key={b} value={b.toLowerCase()}>{b}</option>
                  ))}
                </select>
              </div>

              {/* Added Date From */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Added Date From</label>
                <input
                  type="date"
                  value={filters.addedDateFrom}
                  onChange={e => updateFilter('addedDateFrom', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
              </div>

              {/* Added Date To */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Added Date To</label>
                <input
                  type="date"
                  value={filters.addedDateTo}
                  onChange={e => updateFilter('addedDateTo', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
              </div>

            </div>
          )}

          {/* Selection Controls + Send Button */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  const newIds = filteredCandidates.map(c => c.id);
                  setSelectedCandidatesForEmail(prev => [...new Set([...prev, ...newIds])]);
                }}
                className="px-4 py-2 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium"
              >
                Select All
              </button>
              <button
                onClick={() => setSelectedCandidatesForEmail([])}
                className="px-4 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                Clear Selection
              </button>
            </div>

            <button
              onClick={() => {
                const selected = recentCandidates.filter(c => selectedCandidatesForEmail.includes(c.id));
                sendAccountManagerNotification(selected);
              }}
              disabled={selectedCandidatesForEmail.length === 0 || sendingAccountManagerEmail}
              className="px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center space-x-2"
            >
              {sendingAccountManagerEmail ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Send Notifications ({selectedCandidatesForEmail.length})</span>
                </>
              )}
            </button>
          </div>

          {/* Candidates Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto max-h-96">
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={
                          filteredCandidates.length > 0 &&
                          filteredCandidates.every(c => selectedCandidatesForEmail.includes(c.id))
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            const newIds = filteredCandidates.map(c => c.id);
                            setSelectedCandidatesForEmail(prev => [...new Set([...prev, ...newIds])]);
                          } else {
                            const filteredIds = new Set(filteredCandidates.map(c => c.id));
                            setSelectedCandidatesForEmail(prev => prev.filter(id => !filteredIds.has(id)));
                          }
                        }}
                        className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Candidate</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">BU</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Added Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Managers</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredCandidates.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                        {activeFilterCount > 0
                          ? 'No candidates match the current filters.'
                          : 'No candidates were added in the last 30 days.'}
                      </td>
                    </tr>
                  ) : (
                    filteredCandidates.map((candidate) => (
                      <tr key={candidate.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <input
                            type="checkbox"
                            checked={selectedCandidatesForEmail.includes(candidate.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedCandidatesForEmail([...selectedCandidatesForEmail, candidate.id]);
                              } else {
                                setSelectedCandidatesForEmail(selectedCandidatesForEmail.filter(id => id !== candidate.id));
                              }
                            }}
                            className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center mr-3">
                              <span className="text-white font-bold text-xs">
                                {(candidate.candidate_name || candidate.candidateName || '?')
                                  .split(' ').map(n => n[0]).join('')}
                              </span>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {candidate.candidate_name || candidate.candidateName}
                              </div>
                              <div className="text-xs text-gray-500">{candidate.client}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {candidate.candidate_email || candidate.candidateEmail}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {candidate.bu || candidate.business_unit || '—'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {candidate.start_date || candidate.startDate || '—'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {candidate.created_at ? (
                            <div>
                              <div>{new Date(candidate.created_at).toLocaleDateString()}</div>
                              <div className="text-xs text-gray-500">
                                {new Date(candidate.created_at).toLocaleTimeString()}
                              </div>
                            </div>
                          ) : '—'}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {[
                              candidate.account_manager_1,
                              candidate.account_manager_2,
                              candidate.account_manager_3,
                              candidate.account_manager_4,
                              candidate.account_manager_5,
                              candidate.account_manager_6,
                              candidate.account_manager_7,
                              candidate.account_manager_8
                            ].filter(email => email && email.trim()).map((manager, index) => (
                              <span key={index} className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                {manager.split('@')[0]}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AccountManagerModal;